import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SnoozeIcon from '@mui/icons-material/Snooze';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import EmailIcon from '@mui/icons-material/Email';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  regenerateTaskReply,
  polishTaskReply,
  executeTaskDomain,
} from '@src/api/tasks';
import dayjs from 'dayjs';
import { Link as RouterLink } from 'react-router-dom';
import paths from '@src/paths';
import { TextEditor } from '@src/components/core/text-editor/text-editor';

const PRIORITY_LABEL = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

const SORT_FIELD_OPTIONS = [
  { value: 'created_at', label: 'Created date' },
  { value: 'due_date', label: 'Due date' },
];

const SORT_DIRECTION_OPTIONS = [
  { value: 'asc', label: 'Oldest first' },
  { value: 'desc', label: 'Newest first' },
];

const SOURCE_BADGE = {
  inbox: { label: 'Inbox', color: 'secondary' },
  budget: { label: 'Budget', color: 'success' },
  pacing: { label: 'Pacing', color: 'warning' },
  zip: { label: 'ZIP', color: 'info' },
  'fb-push': { label: 'Push to FB', color: 'primary' },
  meeting: { label: 'Meeting', color: 'warning' },
};

function taskStateColor(task) {
  if (task.is_completed) return { color: 'success.main', label: 'Done' };
  if (task.snoozed_until && dayjs(task.snoozed_until).isAfter(dayjs())) {
    return { color: 'warning.main', label: `Snoozed until ${dayjs(task.snoozed_until).format('MMM D')}` };
  }
  return { color: 'grey.500', label: 'Open' };
}

function SnoozeMenu({ task, onSnooze }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClose = () => setAnchorEl(null);

  const isSnoozed = Boolean(
    task.snoozed_until && dayjs(task.snoozed_until).isAfter(dayjs()),
  );

  const handleSnooze = (delta) => {
    handleClose();
    if (delta === null) {
      onSnooze(task, null);
      return;
    }
    const until = dayjs().add(delta.amount, delta.unit).hour(9).minute(0).second(0).toISOString();
    onSnooze(task, until);
  };

  const handleCustom = () => {
    handleClose();
    const input = window.prompt(
      'Snooze until when? (YYYY-MM-DD or YYYY-MM-DD HH:mm)',
      dayjs().add(1, 'week').format('YYYY-MM-DD'),
    );
    if (!input) return;
    const parsed = dayjs(input);
    if (!parsed.isValid()) {
      window.alert('Invalid date. Use YYYY-MM-DD or YYYY-MM-DD HH:mm.');
      return;
    }
    onSnooze(task, parsed.toISOString());
  };

  return (
    <>
      <Tooltip title={isSnoozed ? 'Snoozed — change or wake up' : 'Snooze'}>
        <IconButton
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{ color: isSnoozed ? 'warning.main' : 'text.secondary' }}
        >
          <SnoozeIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem onClick={() => handleSnooze({ amount: 1, unit: 'day' })}>Tomorrow 9am</MenuItem>
        <MenuItem onClick={() => handleSnooze({ amount: 3, unit: 'day' })}>In 3 days</MenuItem>
        <MenuItem onClick={() => {
          handleClose();
          // Next Monday 9am
          const today = dayjs();
          const daysUntilMonday = (8 - today.day()) % 7 || 7;
          const until = today.add(daysUntilMonday, 'day').hour(9).minute(0).second(0).toISOString();
          onSnooze(task, until);
        }}>Next Monday</MenuItem>
        <MenuItem onClick={() => handleSnooze({ amount: 2, unit: 'week' })}>In 2 weeks</MenuItem>
        <MenuItem onClick={handleCustom}>Custom date…</MenuItem>
        {isSnoozed && [
          <Divider key="divider" />,
          <MenuItem key="wake" onClick={() => handleSnooze(null)}>Wake up now</MenuItem>,
        ]}
      </Menu>
    </>
  );
}

SnoozeMenu.propTypes = {
  task: PropTypes.shape({
    id: PropTypes.number,
    snoozed_until: PropTypes.string,
  }).isRequired,
  onSnooze: PropTypes.func.isRequired,
};

/**
 * Overflow menu for the row's secondary actions (Edit, Delete).
 * Shown only on mobile where horizontal space is tight; on desktop the
 * Edit/Delete icons appear inline instead.
 */
function RowOverflowMenu({ task, onEdit, onDelete }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClose = () => setAnchorEl(null);
  return (
    <>
      <Tooltip title="More">
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem onClick={() => { handleClose(); onEdit(task); }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem
          onClick={() => { handleClose(); onDelete(task); }}
          sx={{ color: 'error.main' }}
        >
          <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>
    </>
  );
}

RowOverflowMenu.propTypes = {
  task: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

function DetailBlock({ label, children }) {
  if (!children) {
    return null;
  }
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
        {children}
      </Typography>
    </Box>
  );
}

DetailBlock.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
};

DetailBlock.defaultProps = {
  children: null,
};

/**
 * Compute a deep-link target for a task based on its source.
 * Returns { to, label } or null if no link is available.
 *
 * Source-key shapes used here:
 *   inbox-thread:<thread_id>
 *   budget-period:<client_id>:<period_iso>
 *   budget-adjustment:<client_id>:<period_iso>:<adjustment_id>
 *   budget-pacing:<client_id>:<period_iso>           (follow-up after budget approval)
 *   budget-pacing:turnoff:<adjustment_id>            (follow-up after turn-off approval)
 *   zip-submission:<id>           (no client_id — fall back to task.client)
 *   zip-fb-push:<submission_id>   (follow-up after ZIP approval)
 */
function deepLinkForTask(task) {
  const key = task.source_key || '';

  if (key.startsWith('inbox-thread:')) {
    const threadId = key.split(':', 2)[1];
    if (threadId) {
      return { to: `${paths.clientInbox}?thread=${threadId}`, label: 'Open thread' };
    }
  }

  if (key.startsWith('budget-period:') || key.startsWith('budget-adjustment:')) {
    const parts = key.split(':');
    const clientId = parts[1];
    const period = parts[2];
    if (clientId && period) {
      return {
        to: `${paths.budgetCenter}?client_id=${clientId}&period=${period}`,
        label: 'Open budget',
      };
    }
  }

  if (key.startsWith('budget-pacing:') && task.client) {
    return {
      to: `${paths.facebook.budgetOptimizer}?client_id=${task.client}`,
      label: 'Open Budget Optimizer',
    };
  }

  if (key.startsWith('zip-submission:') && task.client) {
    return {
      to: `${paths.zipSubmissions}?client_id=${task.client}`,
      label: 'Open ZIP review',
    };
  }

  if (key.startsWith('zip-fb-push:') && task.client) {
    return {
      to: `${paths.facebook.campaignBuilder}?client_id=${task.client}`,
      label: 'Open Campaign Builder',
    };
  }

  return null;
}

// Per-task ActivityHistoryDialog was removed — TaskActivity model + endpoint
// stay in place for the future dedicated audit view + calibration data.

// Change-chip colors mirror the Budget Submissions page: green = increase,
// amber = decrease, blue = new segment, red = turn-off.
const CHANGE_CHIP_COLOR = {
  increase: 'success',
  decrease: 'warning',
  new: 'info',
  turn_off: 'error',
};

// Compact integer (1,234,567 → "1.2M") for population deltas.
function formatCompact(value) {
  const n = Number(value || 0);
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 1_000) return `${sign}${(abs / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}k`;
  return `${sign}${abs.toLocaleString()}`;
}

function DomainPreviewCard({ task, onApproved }) {
  const [working, setWorking] = useState(false);
  // Two-step confirm: the first click arms, the second executes. Prevents a
  // stray click from firing a real budget push, and makes "did it go through?"
  // unambiguous (you consciously confirm, then get a success toast).
  const [confirming, setConfirming] = useState(false);
  const confirmTimer = useRef(null);
  const preview = task.domain_preview;

  useEffect(() => () => {
    if (confirmTimer.current) clearTimeout(confirmTimer.current);
  }, []);

  if (!preview) return null;

  const successLabel = () => {
    if (preview.kind === 'budget_period') {
      return `Budget approved${preview.period_label ? ` — ${preview.period_label}` : ''} (${preview.total}).`;
    }
    if (preview.kind === 'budget_adjustment') return 'Budget adjustment approved.';
    if (preview.kind === 'zip_submission') return 'ZIP submission approved.';
    return 'Approved & executed.';
  };

  const armConfirm = () => {
    setConfirming(true);
    if (confirmTimer.current) clearTimeout(confirmTimer.current);
    // Auto-disarm after a few seconds so the button doesn't sit "hot".
    confirmTimer.current = setTimeout(() => setConfirming(false), 5000);
  };

  const handleExecute = () => {
    setWorking(true);
    executeTaskDomain(task.id)
      .then((response) => {
        onApproved(response.data?.task || task, successLabel());
      })
      .catch((err) => {
        console.error('Execute domain failed', err);
        const detail = err?.response?.data?.error || err?.response?.data?.detail || 'Action failed.';
        window.alert(detail);
      })
      .finally(() => {
        setWorking(false);
        setConfirming(false);
      });
  };

  const renderBody = () => {
    if (preview.kind === 'budget_period') {
      const segments = preview.segments || [];
      const chips = preview.change_chips || [];
      const hasAdjustments = segments.some((s) => s.adjustments);
      return (
        <Stack spacing={1.25}>
          <Typography variant="body2" color="text.secondary">
            {preview.segment_count} segment{preview.segment_count === 1 ? '' : 's'} · Period: <strong>{preview.period_label}</strong> · Total: <strong>{preview.total}</strong>
          </Typography>

          {/* What changed vs the currently-running budget — the headline chips
              from the Budget Submissions page. */}
          {chips.length ? (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {chips.map((c, i) => (
                <Tooltip
                  key={`${c.segment}-${i}`}
                  title={c.from && c.to ? `${c.segment}: ${c.from} → ${c.to}` : c.segment}
                >
                  <Chip size="small" color={CHANGE_CHIP_COLOR[c.type] || 'default'} label={c.label} />
                </Tooltip>
              ))}
            </Stack>
          ) : null}

          {/* Per-segment Starting → Adjustments → Effective, like the page. */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: hasAdjustments ? '1fr auto auto auto' : '1fr auto auto',
              columnGap: 2,
              rowGap: 0.5,
              alignItems: 'baseline',
            }}
          >
            <Typography variant="caption" color="text.secondary">Segment</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right' }}>Starting</Typography>
            {hasAdjustments ? (
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right' }}>Adjust.</Typography>
            ) : null}
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right' }}>Effective</Typography>
            {segments.map((s) => (
              <Fragment key={s.id}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                    {s.label || `${s.vertical}${s.location ? ` · ${s.location}` : ''}`}
                  </Typography>
                  {s.has_pending_turn_off ? (
                    <Chip size="small" color="error" variant="outlined" label="Turn-off pending" sx={{ mt: 0.25 }} />
                  ) : null}
                </Box>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', textAlign: 'right' }}>
                  {s.starting}
                </Typography>
                {hasAdjustments ? (
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', textAlign: 'right', color: 'text.secondary' }}>
                    {s.adjustments || '—'}
                  </Typography>
                ) : null}
                <Typography variant="body2" sx={{ fontFamily: 'monospace', textAlign: 'right', fontWeight: 600 }}>
                  {s.effective}
                </Typography>
              </Fragment>
            ))}
          </Box>
        </Stack>
      );
    }
    if (preview.kind === 'budget_adjustment') {
      return (
        <Stack spacing={0.5}>
          <Typography variant="body2">
            <strong>{preview.segment_label}</strong> · {preview.period}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Current: <strong>{preview.current_amount}</strong> · Change: <strong>{preview.amount_delta}</strong>
            {preview.new_amount ? <> · New: <strong>{preview.new_amount}</strong></> : null}
          </Typography>
          {preview.is_turn_off ? (
            <Chip size="small" color="warning" label="Turn-off request" sx={{ alignSelf: 'flex-start', mt: 0.5 }} />
          ) : null}
          {preview.notes ? (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>{preview.notes}</Typography>
          ) : null}
        </Stack>
      );
    }
    if (preview.kind === 'zip_submission') {
      const impact = preview.impact;
      const netPop = impact ? impact.net_population_change : 0;
      return (
        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">
            {preview.territory ? `${preview.territory} · ` : ''}{preview.vertical || ''} · <strong>{preview.adds_count} adds</strong> · <strong>{preview.removes_count} removes</strong>
          </Typography>

          {/* Before → after territory impact, same as the ZIP Submissions page. */}
          {impact ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'auto auto auto',
                columnGap: 2,
                rowGap: 0.25,
                alignItems: 'baseline',
                width: 'fit-content',
              }}
            >
              <Typography variant="caption" color="text.secondary" />
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right' }}>ZIPs</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right' }}>Population</Typography>

              <Typography variant="body2" color="text.secondary">Now</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', textAlign: 'right' }}>{impact.current_zip_count.toLocaleString()}</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', textAlign: 'right' }}>{formatCompact(impact.current_population)}</Typography>

              <Typography variant="body2" color="text.secondary">After</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', textAlign: 'right', fontWeight: 600 }}>{impact.after_zip_count.toLocaleString()}</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', textAlign: 'right', fontWeight: 600 }}>{formatCompact(impact.after_population)}</Typography>
            </Box>
          ) : null}
          {impact ? (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                variant="outlined"
                color={netPop < 0 ? 'warning' : 'success'}
                label={`${netPop >= 0 ? '+' : ''}${formatCompact(netPop)} reach`}
              />
              <Chip size="small" variant="outlined" label={`${impact.pct_zips_changed}% of ZIPs changed`} />
            </Stack>
          ) : null}
          {impact && impact.warnings?.length ? (
            <Stack spacing={0.5}>
              {impact.warnings.map((w, i) => (
                <Typography key={i} variant="caption" color="warning.main">⚠ {w}</Typography>
              ))}
            </Stack>
          ) : null}

          {preview.submitter_notes ? (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {preview.submitter_notes}
            </Typography>
          ) : null}
          <Stack direction="row" spacing={2}>
            {preview.adds_count ? (
              <Box>
                <Typography variant="caption" color="text.secondary">ADDS</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {(preview.adds_sample || []).join(', ')}
                  {preview.adds_count > (preview.adds_sample || []).length ? ' …' : ''}
                </Typography>
              </Box>
            ) : null}
            {preview.removes_count ? (
              <Box>
                <Typography variant="caption" color="text.secondary">REMOVES</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {(preview.removes_sample || []).join(', ')}
                  {preview.removes_count > (preview.removes_sample || []).length ? ' …' : ''}
                </Typography>
              </Box>
            ) : null}
          </Stack>
        </Stack>
      );
    }
    if (preview.kind === 'zip_fb_push') {
      return (
        <Typography variant="body2" color="text.secondary">
          ZIP changes are approved. Push to Facebook campaigns must be done in Campaign Builder.
        </Typography>
      );
    }
    if (preview.kind === 'budget_pacing') {
      return (
        <Typography variant="body2" color="text.secondary">
          Budget approved. Confirm pacing in Budget Optimizer.
        </Typography>
      );
    }
    return null;
  };

  const canInlineExecute = !preview.needs_review; // FB-push + pacing require domain UI

  return (
    <Box sx={{ px: 2, py: 2.5, backgroundColor: (theme) => theme.palette.action.hover }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Chip size="small" label={preview.kind.replace(/_/g, ' ')} color="info" />
      </Stack>

      <Box sx={{ mb: 2 }}>{renderBody()}</Box>

      <Stack direction="row" spacing={1} alignItems="center">
        {canInlineExecute ? (
          confirming ? (
            <>
              <Button
                variant="contained"
                color="warning"
                disabled={working}
                onClick={handleExecute}
                startIcon={<CheckCircleIcon />}
              >
                {working ? 'Applying…' : 'Confirm — apply now'}
              </Button>
              <Button variant="text" color="inherit" disabled={working} onClick={() => setConfirming(false)}>
                Cancel
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              color="success"
              disabled={working}
              onClick={armConfirm}
              startIcon={<CheckCircleIcon />}
            >
              {preview.execute_label || 'Approve'}
            </Button>
          )
        ) : null}
        {preview.deep_link && !confirming ? (
          <Button
            variant={canInlineExecute ? 'outlined' : 'contained'}
            component={RouterLink}
            to={preview.deep_link}
            startIcon={<OpenInNewIcon />}
          >
            {canInlineExecute ? 'Open in domain page' : (preview.execute_label || 'Open')}
          </Button>
        ) : null}
      </Stack>
      {confirming ? (
        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
          This approves and applies the change now. Click “Confirm” to proceed, or Cancel.
        </Typography>
      ) : null}
    </Box>
  );
}

DomainPreviewCard.propTypes = {
  task: PropTypes.object.isRequired,
  onApproved: PropTypes.func.isRequired,
};


function SourceEmailPanel({ sourceEmail }) {
  const [expanded, setExpanded] = useState(false);
  if (!sourceEmail) return null;
  const recipients = [
    ...(sourceEmail.to_addresses || []).map((a) => `To: ${a}`),
    ...(sourceEmail.cc_addresses || []).map((a) => `Cc: ${a}`),
  ];

  return (
    <Paper variant="outlined" sx={{ mb: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        sx={{ p: 1.25, cursor: 'pointer' }}
        onClick={() => setExpanded((v) => !v)}
        spacing={1}
      >
        <EmailIcon fontSize="small" color="action" />
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
            {sourceEmail.subject || '(no subject)'}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            from {sourceEmail.from_email || '(unknown)'}{sourceEmail.received_at ? ` · ${dayjs(sourceEmail.received_at).fromNow()}` : ''}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={expanded ? 'Hide' : 'Show'}
          variant="outlined"
          onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
        />
      </Stack>
      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 2 }}>
          {recipients.length ? (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              {recipients.map((r) => (
                <Chip key={r} size="small" label={r} variant="outlined" />
              ))}
            </Stack>
          ) : null}
          {sourceEmail.body_excerpt ? (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}>
              {sourceEmail.body_excerpt}
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary">(no body)</Typography>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}

SourceEmailPanel.propTypes = {
  sourceEmail: PropTypes.object,
};

SourceEmailPanel.defaultProps = { sourceEmail: null };


function ProposalCard({ task, onApprove, onReject, onRefreshTask }) {
  const [working, setWorking] = useState(false);
  const [draftReply, setDraftReply] = useState(task.ai_proposed_reply || '');
  const [regenerating, setRegenerating] = useState(false);
  const [polishing, setPolishing] = useState(false);
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  // Voice input refs — same pattern as AgentChatPage so interim words show
  // live without the appending-repetition bug from naive state setters.
  const voiceBaseRef = useRef('');
  const voiceFinalsRef = useRef('');

  // Keep local draft in sync when task changes (e.g., after regenerate).
  useEffect(() => {
    setDraftReply(task.ai_proposed_reply || '');
  }, [task.id, task.ai_proposed_reply, task.updated_at]);

  const actions = Array.isArray(task.ai_proposed_actions) ? task.ai_proposed_actions : [];
  const subtasks = Array.isArray(task.subtasks) ? task.subtasks : [];
  const hasReply = Boolean((task.ai_proposed_reply || '').trim());
  const isInboxTask = (task.source_key || '').startsWith('inbox-thread:');
  const isReplyEdited = (draftReply || '').trim() !== (task.ai_proposed_reply || '').trim();

  const handleApprove = () => {
    setWorking(true);
    const payload = isReplyEdited ? { reply: draftReply } : {};
    Promise.resolve(onApprove(task, payload)).finally(() => setWorking(false));
  };

  const handleReplyKeyDown = (event) => {
    // Cmd+Enter (mac) or Ctrl+Enter (win/linux) approves
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      if (!working) handleApprove();
    }
  };
  const handleReject = () => {
    const reason = window.prompt('Reject reason (optional):', '') || '';
    setWorking(true);
    Promise.resolve(onReject(task, reason)).finally(() => setWorking(false));
  };

  const handleRegenerate = () => {
    setRegenerating(true);
    regenerateTaskReply(task.id)
      .then((response) => {
        const updated = response.data;
        setDraftReply(updated.ai_proposed_reply || '');
        if (onRefreshTask) onRefreshTask(updated);
      })
      .catch((err) => {
        console.error('Regenerate failed', err);
        window.alert('Failed to regenerate draft.');
      })
      .finally(() => setRegenerating(false));
  };

  const handlePolish = () => {
    if (!draftReply.trim()) return;
    setPolishing(true);
    polishTaskReply(task.id, draftReply)
      .then((response) => {
        const updated = response.data;
        setDraftReply(updated.ai_proposed_reply || '');
        if (onRefreshTask) onRefreshTask(updated);
      })
      .catch((err) => {
        console.error('Polish failed', err);
        window.alert('Failed to polish draft.');
      })
      .finally(() => setPolishing(false));
  };

  const startListening = () => {
    const SpeechRecognition =
      typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
    if (!SpeechRecognition) {
      window.alert('Voice input not supported in this browser.');
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true; // stream words live as they come in
    recognition.lang = 'en-US';

    // Snapshot whatever's already in the draft so existing text stays put.
    voiceBaseRef.current = draftReply || '';
    voiceFinalsRef.current = '';

    const compose = (interim) => {
      const base = voiceBaseRef.current;
      const finals = voiceFinalsRef.current;
      const parts = [];
      if (base) parts.push(base);
      if (finals) parts.push(finals);
      if (interim) parts.push(interim);
      if (parts.length === 0) return '';
      // Join with a space when the previous chunk doesn't already end in
      // whitespace so the live stream reads naturally.
      let out = parts[0];
      for (let i = 1; i < parts.length; i += 1) {
        const piece = parts[i].trimStart();
        if (!piece) continue;
        if (out && !/\s$/.test(out)) {
          out += ' ';
        }
        out += piece;
      }
      return out;
    };

    recognition.onresult = (event) => {
      let interimPart = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          const trimmed = transcript.trim();
          if (trimmed) {
            const sep = voiceFinalsRef.current && !/\s$/.test(voiceFinalsRef.current)
              ? ' ' : '';
            voiceFinalsRef.current = `${voiceFinalsRef.current}${sep}${trimmed}`;
          }
        } else {
          interimPart += transcript;
        }
      }
      setDraftReply(compose(interimPart));
    };
    recognition.onerror = () => setListening(false);
    recognition.onend = () => {
      // Drop interim and keep only the finalized accumulation.
      setDraftReply(compose(''));
      setListening(false);
    };
    recognition.start();
    recognitionRef.current = recognition;
    setListening(true);
  };
  const stopListening = () => {
    if (recognitionRef.current) recognitionRef.current.stop();
    setListening(false);
  };

  useEffect(() => () => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (_e) { /* ignore */ }
    }
  }, []);

  // Build the "what will happen" preview. Dedupe: if there's an explicit
  // `send_reply` action, the reply IS that action — don't double-count.
  const previewLines = [];
  const hasExplicitReplyAction = actions.some((a) => a && a.type === 'send_reply');
  if ((hasReply || isReplyEdited) && !hasExplicitReplyAction) {
    previewLines.push('Send the reply above (saved as draft on the linked inbox thread).');
  }
  actions.forEach((a) => {
    if (a && a.type === 'send_reply') {
      previewLines.push(`Send reply: ${a.summary || 'send the reply above'}`);
    } else {
      previewLines.push(`Execute: ${a && (a.summary || a.type || 'action')}`);
    }
  });
  if (subtasks.length) {
    previewLines.push(`Mark this task + ${subtasks.length} subtask${subtasks.length === 1 ? '' : 's'} as completed.`);
  } else {
    previewLines.push('Mark this task as completed.');
  }

  return (
    <Box sx={{ px: 2, py: 2.5, backgroundColor: (theme) => theme.palette.action.hover }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
        <Chip size="small" label="AI proposal" color="primary" />
        {task.ai_confidence != null ? (
          <Chip size="small" label={`${task.ai_confidence}% confidence`} variant="outlined" />
        ) : null}
        {isReplyEdited ? (
          <Chip size="small" label="Edited" color="warning" variant="outlined" />
        ) : null}
      </Stack>

      <SourceEmailPanel sourceEmail={task.source_email} />

      {task.ai_reasoning ? (
        <Box sx={{ mb: 2 }}>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.4 }}>
            AI reasoning
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
            {task.ai_reasoning}
          </Typography>
        </Box>
      ) : null}

      {actions.length ? (
        <Box sx={{ mb: 2 }}>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.4 }}>
            Proposed action{actions.length === 1 ? '' : 's'}
          </Typography>
          <Stack spacing={1} sx={{ mt: 0.5 }}>
            {actions.map((a, idx) => (
              <Paper key={idx} variant="outlined" sx={{ p: 1.25 }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                  {a.type || 'action'}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.25 }}>
                  {a.summary || JSON.stringify(a)}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </Box>
      ) : null}

      {subtasks.length ? (
        <Box sx={{ mb: 2 }}>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.4 }}>
            Subtasks ({subtasks.length})
          </Typography>
          <Stack spacing={1} sx={{ mt: 0.5 }}>
            {subtasks.map((st) => (
              <Paper key={st.id} variant="outlined" sx={{ p: 1.25 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {st.title}
                </Typography>
                {st.notes ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {st.notes}
                  </Typography>
                ) : null}
              </Paper>
            ))}
          </Stack>
        </Box>
      ) : null}

      {(hasReply || isInboxTask) ? (
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.4 }}>
              Proposed reply (editable)
            </Typography>
            <Stack direction="row" spacing={0.5}>
              <Tooltip title={listening ? 'Stop dictation' : 'Dictate (voice → text)'}>
                <span>
                  <IconButton
                    size="small"
                    onClick={listening ? stopListening : startListening}
                    color={listening ? 'error' : 'default'}
                  >
                    {listening ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Polish raw text into a professional reply">
                <span>
                  <IconButton
                    size="small"
                    onClick={handlePolish}
                    disabled={polishing || !draftReply.trim()}
                  >
                    {polishing ? <CircularProgress size={16} /> : <AutoFixHighIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Regenerate AI draft from scratch">
                <span>
                  <IconButton
                    size="small"
                    onClick={handleRegenerate}
                    disabled={regenerating}
                  >
                    {regenerating ? <CircularProgress size={16} /> : <AutorenewIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
          <Box
            onKeyDown={handleReplyKeyDown}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 1,
              minHeight: 140,
              opacity: regenerating || polishing ? 0.6 : 1,
              pointerEvents: regenerating || polishing ? 'none' : 'auto',
              '& .ProseMirror': { minHeight: 100, outline: 'none' },
              '& .ProseMirror p': { margin: '0 0 0.5em 0' },
              '& .ProseMirror p:last-child': { marginBottom: 0 },
              '& .is-editor-empty:first-of-type::before': {
                content: 'attr(data-placeholder)',
                color: 'text.disabled',
                float: 'left',
                height: 0,
                pointerEvents: 'none',
              },
            }}
          >
            <TextEditor
              key={`reply-editor-${task.id}`}
              content={draftReply || ''}
              onUpdate={({ editor }) => setDraftReply(editor.getHTML())}
              placeholder={listening ? 'Listening… speak now.' : 'Type, dictate, or regenerate. Press ⌘+Enter to approve.'}
            />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            ⌘+Enter (or Ctrl+Enter) to approve. Edits are saved with the approval.
          </Typography>
        </Box>
      ) : null}

      <Box sx={{ mb: 2 }}>
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.4 }}>
          Approve & Execute will:
        </Typography>
        <Stack component="ol" sx={{ pl: 2.5, mt: 0.5, mb: 0 }}>
          {previewLines.map((line, idx) => (
            <Typography key={idx} variant="body2" component="li" sx={{ mb: 0.25 }}>
              {line}
            </Typography>
          ))}
        </Stack>
      </Box>

      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          color="success"
          disabled={working}
          onClick={handleApprove}
          startIcon={<CheckCircleIcon />}
        >
          {isReplyEdited ? 'Approve edited & Execute' : 'Approve & Execute'}
        </Button>
        <Button
          variant="outlined"
          color="error"
          disabled={working}
          onClick={handleReject}
        >
          Reject
        </Button>
      </Stack>
    </Box>
  );
}

ProposalCard.propTypes = {
  task: PropTypes.object.isRequired,
  onApprove: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
  onRefreshTask: PropTypes.func,
};

ProposalCard.defaultProps = {
  onRefreshTask: null,
};

function TaskTable({
  tasks,
  loading,
  sortConfig,
  selectedIds,
  onEdit,
  onToggleComplete,
  onDelete,
  onSortChange,
  onSelectionChange,
  onBulkDelete,
  onBulkComplete,
  onSnooze,
  onApproveProposal,
  onRejectProposal,
  onDomainApproved,
  onRefreshTask,
}) {
  const [expandedRows, setExpandedRows] = useState({});
  const lastClickedIndexRef = useRef(null);

  const taskIds = useMemo(() => tasks.map((t) => t.id), [tasks]);
  const allSelected = taskIds.length > 0 && taskIds.every((id) => selectedIds.has(id));
  const someSelected = !allSelected && taskIds.some((id) => selectedIds.has(id));

  const toggleExpanded = (taskId) => {
    setExpandedRows((prev) => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  const handleRowSelect = (taskId, index, event) => {
    const next = new Set(selectedIds);
    const isShift = event.nativeEvent && event.nativeEvent.shiftKey;
    const lastIndex = lastClickedIndexRef.current;

    if (isShift && lastIndex !== null && lastIndex !== undefined) {
      const start = Math.min(lastIndex, index);
      const end = Math.max(lastIndex, index);
      const shouldSelect = !next.has(taskId);
      for (let i = start; i <= end; i += 1) {
        const id = taskIds[i];
        if (id == null) continue;
        if (shouldSelect) {
          next.add(id);
        } else {
          next.delete(id);
        }
      }
    } else if (next.has(taskId)) {
      next.delete(taskId);
    } else {
      next.add(taskId);
    }
    lastClickedIndexRef.current = index;
    onSelectionChange(next);
  };

  const handleSelectAll = () => {
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(taskIds));
    }
    lastClickedIndexRef.current = null;
  };

  const expandedCount = useMemo(
    () => Object.values(expandedRows).filter(Boolean).length,
    [expandedRows],
  );

  const selectedCount = selectedIds.size;

  return (
    <Card variant="outlined">
      <CardHeader
        title="Table view"
        subheader={
          selectedCount
            ? `${selectedCount} selected. Shift-click to extend the range.`
            : `Built for triage and detail review. ${expandedCount ? `${expandedCount} row${expandedCount === 1 ? '' : 's'} expanded.` : ''}`
        }
        action={
          selectedCount ? (
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => onSelectionChange(new Set())}
              >
                Clear
              </Button>
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={onBulkComplete}
              >
                Complete {selectedCount}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineIcon />}
                onClick={onBulkDelete}
              >
                Delete {selectedCount}
              </Button>
            </Stack>
          ) : (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ minWidth: { md: 380 } }}>
              <TextField
                select
                size="small"
                label="Sort by"
                value={sortConfig.field}
                onChange={(event) => onSortChange(event.target.value, sortConfig.direction)}
              >
                {SORT_FIELD_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Order"
                value={sortConfig.direction}
                onChange={(event) => onSortChange(sortConfig.field, event.target.value)}
              >
                {SORT_DIRECTION_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          )
        }
      />
      <CardContent>
        {loading ? (
          <Box sx={{ py: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : tasks.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No tasks found. Try adjusting your filters or create a new task.
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell padding="checkbox" />
                  <TableCell>Task</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Source</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Client</TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Assignee</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Due date</TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Created</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Priority</TableCell>
                  <TableCell padding="checkbox">State</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tasks.map((task, index) => {
                  const isExpanded = Boolean(expandedRows[task.id]);
                  const isSelected = selectedIds.has(task.id);
                  const subtaskCount = task.subtasks_count ?? (task.subtasks || []).length;
                  const hasProposal = Boolean(
                    (task.ai_proposed_actions && task.ai_proposed_actions.length) ||
                    (task.ai_proposed_reply || '').trim(),
                  );
                  const hasDetails = Boolean(
                    hasProposal ||
                    task.domain_preview ||
                    task.source_key ||
                    task.notes ||
                    task.description ||
                    subtaskCount,
                  );
                  const sourceBadge = task.source_domain ? SOURCE_BADGE[task.source_domain] : null;
                  const deepLink = deepLinkForTask(task);

                  return (
                    <Fragment key={task.id}>
                      <TableRow hover selected={isSelected}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isSelected}
                            onClick={(event) => handleRowSelect(task.id, index, event)}
                            onChange={() => {}}
                          />
                        </TableCell>
                        <TableCell padding="checkbox">
                          <IconButton
                            size="small"
                            onClick={() => toggleExpanded(task.id)}
                            disabled={!hasDetails}
                          >
                            {isExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ minWidth: { xs: 0, md: 280 }, maxWidth: { xs: '100%', md: 420 } }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {task.title}
                          </Typography>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                            {/* Source badge moves into the title cell on mobile since the Source column is hidden. */}
                            <Box sx={{ display: { xs: 'inline-flex', md: 'none' } }}>
                              {sourceBadge ? (
                                <Chip size="small" label={sourceBadge.label} color={sourceBadge.color} variant="outlined" />
                              ) : (
                                <Chip size="small" label="Manual" variant="outlined" />
                              )}
                            </Box>
                            {hasProposal ? (
                              <Chip
                                size="small"
                                label={task.ai_confidence != null ? `🤖 ${task.ai_confidence}%` : '🤖 AI proposal'}
                                color="primary"
                              />
                            ) : null}
                            {subtaskCount ? (
                              <Chip size="small" label={`${subtaskCount} subtask${subtaskCount === 1 ? '' : 's'}`} variant="outlined" />
                            ) : null}
                          </Stack>
                          {/* Mobile-only meta line: client · due · priority — since those columns are hidden. */}
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: { xs: 'block', md: 'none' }, mt: 0.5 }}
                          >
                            {[
                              task.client_name,
                              task.due_date ? `Due ${dayjs(task.due_date).format('MMM D')}` : null,
                              PRIORITY_LABEL[task.priority] || null,
                            ].filter(Boolean).join(' · ')}
                          </Typography>
                          {task.notes ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }} noWrap>
                              {task.notes}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell sx={{ minWidth: 110, display: { xs: 'none', md: 'table-cell' } }}>
                          {sourceBadge ? (
                            <Chip
                              size="small"
                              label={sourceBadge.label}
                              color={sourceBadge.color}
                              variant="outlined"
                            />
                          ) : (
                            <Chip size="small" label="Manual" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{task.client_name || '—'}</TableCell>
                        <TableCell sx={{ minWidth: 160, display: { xs: 'none', lg: 'table-cell' } }}>
                          {task.assignee_name ? (
                            <Chip size="small" color="primary" variant="outlined" label={task.assignee_name} />
                          ) : (
                            <Chip size="small" label="Unassigned" />
                          )}
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          {task.due_date ? dayjs(task.due_date).format('MMM D, YYYY') : '—'}
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                          {task.created_at ? (
                            <Tooltip title={dayjs(task.created_at).format('MMM D, YYYY h:mm A')}>
                              <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                                {dayjs(task.created_at).fromNow()}
                              </Typography>
                            </Tooltip>
                          ) : '—'}
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{PRIORITY_LABEL[task.priority] || task.priority_display || '—'}</TableCell>
                        <TableCell padding="checkbox">
                          {(() => {
                            const state = taskStateColor(task);
                            return (
                              <Tooltip title={state.label}>
                                <Box
                                  sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    backgroundColor: state.color,
                                    mx: 'auto',
                                  }}
                                />
                              </Tooltip>
                            );
                          })()}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={task.is_completed ? 'Reopen' : 'Mark done'}>
                            <IconButton
                              size="small"
                              onClick={() => onToggleComplete(task, !task.is_completed)}
                              sx={{ color: task.is_completed ? 'success.main' : 'text.secondary' }}
                            >
                              {task.is_completed ? (
                                <CheckCircleIcon fontSize="small" />
                              ) : (
                                <CheckCircleOutlineIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                          <SnoozeMenu task={task} onSnooze={onSnooze} />
                          {deepLink ? (
                            <Tooltip title={deepLink.label}>
                              <IconButton
                                size="small"
                                component={RouterLink}
                                to={deepLink.to}
                                sx={{ color: 'primary.main' }}
                              >
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          {/* Mobile: collapse Edit + Delete behind a kebab menu so
                              Done + Snooze + Open get the row's limited width. */}
                          <Box sx={{ display: { xs: 'inline-flex', sm: 'none' } }}>
                            <RowOverflowMenu task={task} onEdit={onEdit} onDelete={onDelete} />
                          </Box>
                          <Tooltip title="Edit task">
                            <IconButton
                              size="small"
                              onClick={() => onEdit(task)}
                              sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete task">
                            <IconButton
                              size="small"
                              onClick={() => onDelete(task)}
                              sx={{ color: 'error.main', display: { xs: 'none', sm: 'inline-flex' } }}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={11} sx={{ py: 0, borderBottom: isExpanded ? undefined : 0 }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            {(task.ai_proposed_actions && task.ai_proposed_actions.length) || (task.ai_proposed_reply || '').trim() ? (
                              <ProposalCard
                                task={task}
                                onApprove={onApproveProposal}
                                onReject={onRejectProposal}
                                onRefreshTask={onRefreshTask}
                              />
                            ) : task.domain_preview ? (
                              <DomainPreviewCard
                                task={task}
                                onApproved={(updated, label) => {
                                  if (onDomainApproved) onDomainApproved(updated, label);
                                  else if (onRefreshTask) onRefreshTask(updated);
                                }}
                              />
                            ) : (
                              <Box sx={{ px: 2, py: 2.5, backgroundColor: (theme) => theme.palette.action.hover }}>
                                <Stack spacing={2.5}>
                                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                                    <Box sx={{ flex: 1 }}>
                                      <DetailBlock label="What needs to be done">
                                        {task.notes || task.description || 'No task notes available.'}
                                      </DetailBlock>
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                      <DetailBlock label="Assignee">
                                        {task.assignee_name || 'Unassigned'}
                                      </DetailBlock>
                                      {task.source_key ? (
                                        <DetailBlock label="Source key">{task.source_key}</DetailBlock>
                                      ) : null}
                                    </Box>
                                  </Stack>
                                </Stack>
                              </Box>
                            )}
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}

TaskTable.propTypes = {
  tasks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      source_key: PropTypes.string,
      source_domain: PropTypes.string,
      subtasks_count: PropTypes.number,
      snoozed_until: PropTypes.string,
    }),
  ),
  loading: PropTypes.bool,
  sortConfig: PropTypes.shape({
    field: PropTypes.oneOf(['created_at', 'due_date']).isRequired,
    direction: PropTypes.oneOf(['asc', 'desc']).isRequired,
  }).isRequired,
  selectedIds: PropTypes.instanceOf(Set).isRequired,
  onEdit: PropTypes.func.isRequired,
  onToggleComplete: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
  onSelectionChange: PropTypes.func.isRequired,
  onBulkDelete: PropTypes.func.isRequired,
  onBulkComplete: PropTypes.func.isRequired,
  onSnooze: PropTypes.func.isRequired,
  onApproveProposal: PropTypes.func.isRequired,
  onRejectProposal: PropTypes.func.isRequired,
  onDomainApproved: PropTypes.func,
  onRefreshTask: PropTypes.func,
};

TaskTable.defaultProps = {
  tasks: [],
  loading: false,
  onDomainApproved: null,
  onRefreshTask: null,
};

export default TaskTable;
